import type {TFunction} from 'i18next';
import type {StepItem} from '../../shared/components/ui/Stepper/StepperProps';
import type {
  StepState,
  AssetDetail,
  WorkOrderRow,
  ReplacementInfo,
  PMItem,
  PMTableRow,
  ScheduledActionItem,
  ScheduledActionRow,
  ContactPerson,
  ContactType,
  GetOrganizationsResponse,
  ValidateAssetData,
  PmApiItem,
  PmCycleApiItem,
  SaApiItem,
  PmSaDetailsResponse,
  PriorityLevel,
  DepartmentApiItem,
  BillCodeApiItem,
  MaintShopApiItem,
  FormContactApiItem,
  WorkOrderStatusApiItem,
  AssetMeterApiItem,
  MeterReadingEntry,
  WorkOrderSuccessData,
  RepairFormOptionsApiResponse,
  RepairScheduleApiItem,
  TechnicianApiItem,
  ShiftApiItem,
  PmDueApiItem,
  SaDueApiItem,
  WarrantyItem,
  WarrantyCycle,
  RepairFlowShop,
  RepairFlowWorkOrder,
  WorkOrderSearchApiItem,
  RepairFormControlSyncRequest,
  RepairFormControlSyncResponse,
  ValidateAssetDataResponse,
  TimeStandardApiItem,
  AGCGridApiItem,
  CreateWorkOrderPayload,
  CreateWorkOrderApiData,
  SaveWorkOrderResult,
  CreateRepairPayload,
  CreateRepairInnerData,
  CreateRepairResult,
  WorkOrderWithRepairsRepairItem,
  WorkOrderWithRepairsApiData,
} from './CreateFlowProps';
import type {GetTechniciansListResponse} from '../shop/screens/ShopHomePageProps';
import {
  _AVG_CHAR_FACTOR,
  _LABEL_FONT_SIZE,
  WORK_ORDER_STEP_IDS,
} from './CreateFlowConstant';
import {apiGet, apiPatch, apiPost} from '@features/auth/api/apiClient';
import {globalErrorHandler} from '@shared/error/GlobalErrorController';
import {formatDate, UserTimezone} from '@shared/nativeModules/DateTimeFormatter';
import {endPoints} from '@features/auth/constants/endPoints';
import {ApiResponse} from '@shared/types/api';
import {appConstants} from '@shared/constants/global';
import workOrderFlowData from './mocks/workOrderFlow.json';
import repairFlowData from './mocks/repairFlow.json';
import {techniciansList as mockTechniciansList} from '../shop/mocks/shopHomePage';
import {DropdownOption} from '@shared/components';
import {getAllRepairPriorities} from '../dashboard/screens/DashboardScreen/DashboardController';
import {RepairReasonStatusesProps} from '@features/repairDetails/RepairDetailsScreenProps';

/**
 * Derives the visual state of a step given the currently active step.
 *
 * @param stepId      1-based ID of the step being evaluated.
 * @param currentStep 1-based ID of the currently active step.
 * @returns 'completed' | 'active' | 'inactive'
 */
export const getStepState = (
  stepId: number,
  currentStep: number,
): StepState => {
  if (stepId < currentStep) {
    return 'completed';
  }
  if (stepId === currentStep) {
    return 'active';
  }
  return 'inactive';
};

/**
 * Builds the ordered array of StepItem objects for the Work Order flow,
 * using translated labels from the current i18n instance.
 *
 * @param t - The i18next translation function.
 * @returns StepItem[] ready to pass to the Stepper component.
 */
export const buildWorkOrderSteps = (t: TFunction): StepItem[] => [
  {
    id: WORK_ORDER_STEP_IDS.ASSET_ORGANIZATION_SELECTION,
    label: t('createFlow.workOrder.steps.assetOrganizationSelection'),
    accessibilityLabel: t('createFlow.workOrder.accessibility.stepLabel', {
      number: WORK_ORDER_STEP_IDS.ASSET_ORGANIZATION_SELECTION,
      title: t('createFlow.workOrder.steps.assetOrganizationSelectionA11y'),
    }),
  },
  {
    id: WORK_ORDER_STEP_IDS.ASSET_DETAILS_VIEW,
    label: t('createFlow.workOrder.steps.assetDetailsView'),
    accessibilityLabel: t('createFlow.workOrder.accessibility.stepLabel', {
      number: WORK_ORDER_STEP_IDS.ASSET_DETAILS_VIEW,
      title: t('createFlow.workOrder.steps.assetDetailsView'),
    }),
  },
  {
    id: WORK_ORDER_STEP_IDS.WORK_ORDER_FORM,
    label: t('createFlow.workOrder.steps.workOrderForm'),
    accessibilityLabel: t('createFlow.workOrder.accessibility.stepLabel', {
      number: WORK_ORDER_STEP_IDS.WORK_ORDER_FORM,
      title: t('createFlow.workOrder.steps.workOrderForm'),
    }),
  },
  // {
  //   id: WORK_ORDER_STEP_IDS.REPAIR_SELECTION,
  //   label: t('createFlow.workOrder.steps.repairSelection'),
  //   accessibilityLabel: t('createFlow.workOrder.accessibility.stepLabel', {
  //     number: WORK_ORDER_STEP_IDS.REPAIR_SELECTION,
  //     title: t('createFlow.workOrder.steps.repairSelection'),
  //   }),
  // },
  {
    id: WORK_ORDER_STEP_IDS.REPAIR_CREATION,
    label: t('createFlow.workOrder.steps.repairCreation'),
    accessibilityLabel: t('createFlow.workOrder.accessibility.stepLabel', {
      number: WORK_ORDER_STEP_IDS.REPAIR_CREATION,
      title: t('createFlow.workOrder.steps.repairCreation'),
    }),
  },
];

/**
 * Returns the appropriate accessibility hint string for a step
 * based on its current visual state.
 *
 * @param state - 'active' | 'completed' | 'inactive'
 * @param t     - The i18next translation function.
 * @returns Translated hint string.
 */
export const getStepA11yHint = (state: StepState, t: TFunction): string => {
  switch (state) {
    case 'active':
      return t('createFlow.workOrder.accessibility.stepActive');
    case 'completed':
      return t('createFlow.workOrder.accessibility.stepCompleted');
    case 'inactive':
    default:
      return t('createFlow.workOrder.accessibility.stepPending');
  }
};

/**
 * Fetches the list of organizations available for work order creation.
 *
 * @returns Promise resolving to the organizations list.
 */
export const getOrganizations = async (
  createdUserId: number,
): Promise<GetOrganizationsResponse | undefined> => {
  try {
    if (appConstants.isMock) {
      return workOrderFlowData.data
        .organizations as unknown as GetOrganizationsResponse;
    }
    const {data} = await apiGet<GetOrganizationsResponse>({
      endpoint: endPoints.createFlow.getOrganizations(createdUserId),
    });
    const response = data as ApiResponse<GetOrganizationsResponse>;
    if (response?.data) {
      return response.data;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Fetches the list of organizations available for work order creation.
 *
 * @returns Promise resolving to the organizations list.
 */
export const validateAsset = async (
  assetNumber: string,
  assetOrganization: string,
  technicianId: number,
): Promise<ValidateAssetDataResponse | undefined> => {
  try {
    if (appConstants.isMock) {
      return {
        validateAssetData: workOrderFlowData.data
          .validateAsset as unknown as ValidateAssetData,
        successMessage: workOrderFlowData.message as string,
        success: true
      };
    }
    const {data} = await apiGet<ValidateAssetData>({
      endpoint: endPoints.createFlow.validateAsset(
        assetNumber,
        assetOrganization,
        technicianId,
      ),
    });
    const response = data as ApiResponse<ValidateAssetData>;
    if (response) {
      return {
        validateAssetData: response.data as ValidateAssetData,
        successMessage: response.message ?? '',
        success: response.success ?? false
      };
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Maps a ValidateAssetData response to the AssetDetail shape used by AssetDetailsSection.
 */
export const mapValidatedAssetToAssetDetail = (
  data: ValidateAssetData,
): AssetDetail => ({
  assetNumber: data.assetNumber,
  organization: data.organizationDesc
    ? `${data.organizationDesc} [${data.assetOrganization}]`
    : data.assetOrganization,
  status: data.assetStatusDesc
    ? `${data.assetStatusDesc} [${data.assetStatus}]`
    : data.assetStatus,
  year: String(data.year),
  make: data.make,
  model: data.model,
  license: data.license ?? '',
  vinSerialNumber: data.serialNumber,
  imageUrl: data.assetThumbnail
    ? `data:image/gif;base64,${data.assetThumbnail}`
    : undefined,
  isDateCycleUnderWarranty: data.isDateCycleUnderWarranty,
  isMeterCycleUnderWarranty: data.isMeterCycleUnderWarranty,
});

/**
 * Maps the openWorkOrders array from ValidateAssetData to WorkOrderRow[].
 */
export const mapValidatedAssetToWorkOrders = (
  data: ValidateAssetData,
  userTimezone: UserTimezone = {},
): WorkOrderRow[] =>
  (data.openWorkOrders ?? []).map(wo => ({
    documentNumber: String(wo.documentID),
    dateIn: formatDate(wo.inDate ?? '', userTimezone, 'dateTimeWithSeconds'),
    dateOut: formatDate(wo.outDate ?? '', userTimezone, 'dateTimeWithSeconds'),
    status: `${wo.statusDesc} [${wo.status}]`,
  }));

/**
 * Maps replacementInfo from ValidateAssetData to ReplacementInfo.
 */
export const mapValidatedAssetToReplacement = (
  data: ValidateAssetData,
  userTimezone: UserTimezone = {},
): ReplacementInfo | null => {
  if (!data.replacementInfo) {
    return null;
  }
  return {
    totalPointValue: String(data.replacementInfo.totalPoints),
    originalReplacementDate: formatDate(
      data.replacementInfo.originalReplaceDate ?? '',
      userTimezone,
      'dateTimeWithSeconds',
    ),
    pointScaleUsed: String(data.replacementInfo.pointScale),
  };
};

/**
 * Maps the contacts array from ValidateAssetData to ContactPerson[].
 * 'Current' → 'current', anything else → 'past'.
 */
export const mapValidatedAssetToContacts = (
  data: ValidateAssetData,
): ContactPerson[] =>
  (data.contacts ?? []).map(c => ({
    id: String(c.contactID),
    name: c.fullName,
    role: c.assetRoleDesc,
    phone: c.primaryPhone,
    email: c.email,
    altPhone: c.alternatePhone ?? undefined,
    type: (c.contactType.toLowerCase() === 'current'
      ? 'current'
      : 'past') as ContactType,
  }));

/**
 * Maps the raw organizations API response to Dropdown-compatible options.
 *
 * @param {organizations} - organization items from the API.
 * @returns {DropdownOption[] }
 */
export const prepareOrganizationFilters = (
  organizations: any[],
  t: TFunction,
): DropdownOption[] => {
  return organizations?.map((org: any) => ({
    label: org.organizationDesc,
    value: org.organization,
    desc: `${t('createFlow.workOrder.assetOrganizationStep.code')}${
      org.organization
    }`,
  }));
};

/**
 * Fetches asset details for the given asset number.
 *
 * @returns Promise resolving to the AssetDetail object.
 */
export const getAssetDetails = async (
  _assetNumber?: string,
): Promise<AssetDetail> => {
  try {
    if (appConstants.isMock) {
      return workOrderFlowData.data.assetDetails as AssetDetail;
    }
    const {data} = await apiGet<AssetDetail>({
      endpoint: endPoints.createFlow.getAssetDetails,
    });
    const response = data as ApiResponse<AssetDetail>;
    if (response?.data) {
      return response.data;
    }
    return workOrderFlowData.data.assetDetails as AssetDetail;
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Fetches open work orders for the asset details view.
 *
 * @returns Promise resolving to WorkOrderRow[].
 */
export const getOpenWorkOrders = async (
  _assetNumber?: string,
): Promise<WorkOrderRow[]> => {
  try {
    if (appConstants.isMock) {
      return workOrderFlowData.data.openWorkOrders as WorkOrderRow[];
    }
    const {data} = await apiGet<WorkOrderRow[]>({
      endpoint: endPoints.createFlow.getOpenWorkOrders,
    });
    const response = data as ApiResponse<WorkOrderRow[]>;
    if (response?.data) {
      return response.data;
    }
    return workOrderFlowData.data.openWorkOrders as WorkOrderRow[];
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Fetches replacement information for the asset details view.
 *
 * @returns Promise resolving to ReplacementInfo.
 */
export const getReplacementInfo = async (
  _assetNumber?: string,
): Promise<ReplacementInfo> => {
  try {
    if (appConstants.isMock) {
      return workOrderFlowData.data.replacement as ReplacementInfo;
    }
    const {data} = await apiGet<ReplacementInfo>({
      endpoint: endPoints.createFlow.getReplacementInfo,
    });
    const response = data as ApiResponse<ReplacementInfo>;
    if (response?.data) {
      return response.data;
    }
    return workOrderFlowData.data.replacement as ReplacementInfo;
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Derives a PriorityLevel from `daysUntilDue`.
 *  < 0  → 'urgent'  (overdue)
 *  0–7  → 'high'    (due soon)
 *  > 7  → 'low'
 */
const _pmPriorityFromDays = (daysUntilDue?: number): PriorityLevel => {
  if (daysUntilDue === undefined) {
    return 'low';
  }
  if (daysUntilDue < 0) {
    return 'urgent';
  }
  if (daysUntilDue <= 7) {
    return 'high';
  }
  return 'low';
};

/**
 * Derives the i18n due-message key from `daysUntilDue`.
 */
const _pmDueMessageKey = (daysUntilDue?: number): string => {
  if (daysUntilDue === undefined) {
    return '';
  }
  if (daysUntilDue < 0) {
    return 'pastDueDate';
  }
  if (daysUntilDue > 0) {
    return 'dueInDays';
  }
  return '';
};

/**
 * Joins `pms` with their matching `pmCycles` (linked by assetPmID) and maps
 * the result to the PMItem[] shape consumed by PreventiveMaintenanceSection.
 *
 * - Each PmApiItem becomes one PMItem.
 * - pmCycles entries whose assetPmID matches are collected as PMTableRow[].
 * - pmIsHierarchical === true adds the subtitle "PM is Hierarchical".
 */
export const mapPmsWithCyclesToPMItems = (
  response: PmSaDetailsResponse,
): PMItem[] =>
  response.pms.map((pm: PmApiItem) => {
    const cycles: PmCycleApiItem[] = response.pmCycles.filter(
      (c: PmCycleApiItem) => c.assetPmID === pm.assetPmID,
    );
    const rows: PMTableRow[] = cycles.map((c: PmCycleApiItem) => ({
      pmType: pm.pmType ?? '',
      cycleType: c.cycleType,
      cycleLength: String(c.cycleLength),
      previousDue: c.previous,
      next: c.next,
      dueIn: c.dueIn,
      errors: c.errors ?? '-',
    }));

    const daysUntilDue = pm.daysUntilDue;
    return {
      id: String(pm.assetPmID),
      title: pm.pmType ?? pm.pmCode,
      subtitle: pm.pmIsHierarchical
        ? 'PM is Hierarchical'
        : 'PM is not Hierarchical',
      date: pm.nextDueDate ?? '',
      hours:
        pm.nextDueHours !== undefined
          ? `${pm.nextDueHours.toLocaleString()} hrs`
          : '',
      priority: _pmPriorityFromDays(daysUntilDue),
      dueMessageKey: _pmDueMessageKey(daysUntilDue),
      dueMessageParams:
        daysUntilDue !== undefined && daysUntilDue > 0
          ? {count: daysUntilDue}
          : undefined,
      rows,
    };
  });

/**
 * Maps a PmSaDetailsResponse to the ScheduledActionItem[] shape consumed by ScheduledActionsSection.
 *
 * Each SaApiItem becomes one ScheduledActionItem with a single ScheduledActionRow.
 */
export const mapPmSaDetailsToScheduledActionItems = (
  response: PmSaDetailsResponse,
): ScheduledActionItem[] =>
  response.sas.map((sa: SaApiItem) => {
    const row: ScheduledActionRow | undefined = sa.cycleType
      ? {
          description: sa.saDescription,
          cycleType: sa.cycleType ?? '',
          cycleLength: String(sa.cycleLength ?? ''),
          previousDue: sa.previous ?? '',
          next: sa.next ?? '',
          dueIn: sa.dueIn ?? '',
        }
      : undefined;

    return {
      id: String(sa.saID),
      title: sa.saDescription,
      date: '',
      hours: '',
      priority: 'low' as PriorityLevel,
      dueMessageKey: '',
      rows: row ? [row] : [],
    };
  });

/**
 * Fetches the combined PM and Scheduled Actions details for a given asset
 * from the single /pm-sa-details endpoint, then maps the response into the
 * PMItem[] and ScheduledActionItem[] shapes.
 *
 * @param assetId        The asset's numeric ID.
 * @param userId         The current user's ID (from Redux technician.createdUserId).
 * @param assetStatus    The asset's status code (e.g. "A").
 * @param assetDepartment The asset's department code (e.g. "11").
 * @returns Object with `pmItems` and `scheduledActions`.
 */
export const getPmSaDetails = async (
  assetId: number,
  userId: number | string,
  assetStatus: string,
  assetDepartment: string,
): Promise<{pmItems: PMItem[]; scheduledActions: ScheduledActionItem[]}> => {
  try {
    if (appConstants.isMock) {
      const mock = workOrderFlowData.data
        .pmSaDetails as unknown as PmSaDetailsResponse;
      return {
        pmItems: mapPmsWithCyclesToPMItems(mock),
        scheduledActions: mapPmSaDetailsToScheduledActionItems(mock),
      };
    }
    const {data} = await apiGet<PmSaDetailsResponse>({
      endpoint: endPoints.createFlow.getPmSaDetails(
        assetId,
        userId,
        assetStatus,
        assetDepartment,
      ),
    });
    const response = data as ApiResponse<PmSaDetailsResponse>;
    if (response?.data) {
      return {
        pmItems: mapPmsWithCyclesToPMItems(response.data),
        scheduledActions: mapPmSaDetailsToScheduledActionItems(response.data),
      };
    }
    return {pmItems: [], scheduledActions: []};
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * @deprecated Use getPmSaDetails instead. Kept for backward-compatibility.
 * Fetches preventive maintenance items for the asset details view.
 */
export const getPreventiveMaintenanceItems = async (
  _assetNumber?: string,
): Promise<PMItem[]> => {
  try {
    if (appConstants.isMock) {
      return workOrderFlowData.data.preventiveMaintenance as PMItem[];
    }
    const {data} = await apiGet<PMItem[]>({
      endpoint: endPoints.createFlow.getPreventiveMaintenance,
    });
    const response = data as ApiResponse<PMItem[]>;
    if (response?.data) {
      return response.data;
    }
    return workOrderFlowData.data.preventiveMaintenance as PMItem[];
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * @deprecated Use getPmSaDetails instead. Kept for backward-compatibility.
 * Fetches scheduled action items for the asset details view.
 */
export const getScheduledActionItems = async (
  _assetNumber?: string,
): Promise<ScheduledActionItem[]> => {
  try {
    if (appConstants.isMock) {
      return workOrderFlowData.data.scheduledActions as ScheduledActionItem[];
    }
    const {data} = await apiGet<ScheduledActionItem[]>({
      endpoint: endPoints.createFlow.getScheduledActions,
    });
    const response = data as ApiResponse<ScheduledActionItem[]>;
    if (response?.data) {
      return response.data;
    }
    return workOrderFlowData.data.scheduledActions as ScheduledActionItem[];
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Fetches contact persons for the asset details view.
 *
 * @returns Promise resolving to ContactPerson[].
 */
export const getContacts = async (
  _assetNumber?: string,
): Promise<ContactPerson[]> => {
  try {
    if (appConstants.isMock) {
      return workOrderFlowData.data.contacts as ContactPerson[];
    }
    const {data} = await apiGet<ContactPerson[]>({
      endpoint: endPoints.createFlow.getContacts,
    });
    const response = data as ApiResponse<ContactPerson[]>;
    if (response?.data) {
      return response.data;
    }
    return workOrderFlowData.data.contacts as ContactPerson[];
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Compute the minimum `maxWidth` (px) that wraps `text` to exactly 2 lines
 * at the label font size, with no truncation.
 *
 * Strategy: try every word-boundary split point, choose the split whose
 * longer line is shortest (minimax), then add one font-size unit as a
 * safety buffer against inter-character spacing variation.
 *
 * Uses Inter Regular 16 px average: ~8.5 px/char  → factor 0.53.
 */

// ─── Work Order Form (Step 3) API functions ─────────────────────────────────

/**
 * Fetches maintenance shop options for the Work Order form from /api/LookUps/maint-shops.
 * Uses the `displayLabel` field provided by the API directly as the dropdown label.
 *
 * @returns Promise resolving to DropdownOption[].
 */
export const getWorkOrderMaintShops = async (): Promise<DropdownOption[]> => {
  try {
    if (appConstants.isMock) {
      return workOrderFlowData.data.repairMaintenanceShops as DropdownOption[];
    }
    const {data} = await apiGet<MaintShopApiItem[]>({
      endpoint: endPoints.createFlow.getWorkOrderMaintShops,
    });
    const response = data as ApiResponse<MaintShopApiItem[]>;
    const items: MaintShopApiItem[] | undefined = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(data)
      ? (data as unknown as MaintShopApiItem[])
      : undefined;
    if (items) {
      return items.map(s => ({
          label: s.displayLabel,
          value: s.maintShop,
          // desc stores maintShopID for the numeric ID required by POST /api/workorders (maintShopId)
          desc: String(s.maintShopID),
        }));
    }
    return [];
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Fetches work order status options.
 * Returns mock data when appConstants.isMock is true, otherwise calls the API.
 *
 * @returns Promise resolving to DropdownOption[].
 */
export const getWorkOrderStatuses = async (): Promise<DropdownOption[]> => {
  try {
    if (appConstants.isMock) {
      return workOrderFlowData.data.workOrderStatuses as DropdownOption[];
    }
    const {data} = await apiGet<WorkOrderStatusApiItem[]>({
      endpoint: endPoints.createFlow.getWorkOrderStatuses,
    });
    const response = data as ApiResponse<WorkOrderStatusApiItem[]>;
    const items: WorkOrderStatusApiItem[] | undefined = Array.isArray(
      response?.data,
    )
      ? response.data
      : Array.isArray(data)
      ? (data as unknown as WorkOrderStatusApiItem[])
      : undefined;
    if (items) {
      return items.map(s => ({
        label: s.displayLabel,
        value: s.status,
      }));
    }
    return [];
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Maps a raw DepartmentApiItem array to DropdownOption[].
 * Label: "Description [Code]", value: department code string.
 */
const mapDepartmentsToOptions = (
  items: DepartmentApiItem[],
): DropdownOption[] =>
  items.map(d => ({
    label: `${d.department} [${d.departmentDesc}]`,
    value: d.department,
  }));

/**
 * Fetches department options for a work order from /api/LookUps/departments.
 * Returns mock data when appConstants.isMock is true, otherwise calls the real API
 * and maps the DepartmentApiItem[] response to DropdownOption[].
 *
 * @returns Promise resolving to DropdownOption[].
 */
export const getWorkOrderDepartments = async (): Promise<DropdownOption[]> => {
  try {
    if (appConstants.isMock) {
      return workOrderFlowData.data.workOrderDepartments as DropdownOption[];
    }
    const {data} = await apiGet<DepartmentApiItem[]>({
      endpoint: endPoints.createFlow.getWorkOrderDepartments,
    });
    const response = data as ApiResponse<DepartmentApiItem[]>;
    if (response?.data && Array.isArray(response.data)) {
      return mapDepartmentsToOptions(response.data);
    }
    // Fallback: if the response itself is an array (no wrapper)
    if (Array.isArray(data)) {
      return mapDepartmentsToOptions(data as unknown as DepartmentApiItem[]);
    }
    return [];
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Maps a raw BillCodeApiItem array to DropdownOption[].
 * Label: "Description [Code]", value: bill code string.
 */
const mapBillCodesToOptions = (items: BillCodeApiItem[]): DropdownOption[] =>
  items.map(b => ({
    label: `${b.billCode} [${b.billCodeDesc}]`,
    value: b.billCode,
  }));

/**
 * Fetches bill code options for a work order from /api/LookUps/bill-codes.
 * Returns mock data when appConstants.isMock is true, otherwise calls the real API
 * and maps the BillCodeApiItem[] response to DropdownOption[].
 *
 * @returns Promise resolving to DropdownOption[].
 */
export const getWorkOrderBillCodes = async (): Promise<DropdownOption[]> => {
  try {
    if (appConstants.isMock) {
      return workOrderFlowData.data.workOrderBillCodes as DropdownOption[];
    }
    const {data} = await apiGet<BillCodeApiItem[]>({
      endpoint: endPoints.createFlow.getWorkOrderBillCodes,
    });
    const response = data as ApiResponse<BillCodeApiItem[]>;
    if (response?.data && Array.isArray(response.data)) {
      return mapBillCodesToOptions(response.data);
    }
    // Fallback: bare array response (no wrapper)
    if (Array.isArray(data)) {
      return mapBillCodesToOptions(data as unknown as BillCodeApiItem[]);
    }
    return [];
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Fetches contact options for the work order form from /api/LookUps/assets/{assetId}/contacts.
 * Maps each contact to a DropdownOption using fullName as label and contactID as value.
 *
 * @param assetId - The asset's numeric ID.
 * @returns Promise resolving to DropdownOption[].
 */
export const getWorkOrderFormContacts = async (
  assetId: number,
): Promise<DropdownOption[]> => {
  try {
    if (appConstants.isMock) {
      return workOrderFlowData.data.workOrderFormContacts as DropdownOption[];
    }
    const {data} = await apiGet<FormContactApiItem[]>({
      endpoint: endPoints.createFlow.getWorkOrderFormContacts(assetId),
    });
    const response = data as ApiResponse<FormContactApiItem[]>;
    const items: FormContactApiItem[] | undefined = Array.isArray(
      response?.data,
    )
      ? response.data
      : Array.isArray(data)
      ? (data as unknown as FormContactApiItem[])
      : undefined;
    if (items) {
      return items.map(c => ({
        label: c?.displayLabel,
        value: String(c?.personID),
      }));
    }
    return [];
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Calls POST /api/workorders to create a new work order document.
 *
 * On success:       returns { documentId: <new id>, meterErrors: [] }
 * On meter errors:  returns { documentId: 0, meterErrors: [...] }
 *                   The caller (WorkOrderFormStepThree) surfaces the errors per
 *                   row so the user can select a `validationOption` and retry.
 * On mock:          returns { documentId: 0, meterErrors: [] } (success path).
 *
 * @param payload - Full CreateWorkOrderPayload built from Step-3 form state.
 * @returns Promise<SaveWorkOrderResult>
 */
export const saveWorkOrderDocument = async (
  payload: CreateWorkOrderPayload,
  technicianId: number,
): Promise<SaveWorkOrderResult> => {
  try {
    if (appConstants.isMock) {
      // Mock mode — behave as a successful save
      return {documentId: 0, meterErrors: [], validationErrors: [], successMessage: 'Work order created successfully.'};
    }
    const {data} = await apiPost<CreateWorkOrderApiData>({
      endpoint: endPoints.createFlow.createWorkOrder,
      payload: {...payload, technicianId},
    });
    const response = data as ApiResponse<CreateWorkOrderApiData>;
    const result: CreateWorkOrderApiData | undefined =
      response?.data ?? (data as unknown as CreateWorkOrderApiData);

    // General field-level validation errors (e.g. date constraint failures).
    // The API returns success:false, data:null, errors:[...string].
    const topLevelErrors: string[] = Array.isArray(
      (response as any)?.errors,
    )
      ? ((response as any).errors as string[])
      : [];
    if (topLevelErrors.length > 0) {
      return {documentId: 0, meterErrors: [], validationErrors: topLevelErrors};
    }

    // Meter validation errors — API returns success:false + meterErrors array
    if (result?.meterErrors?.length) {
      return {documentId: 0, meterErrors: result.meterErrors, validationErrors: []};
    }

    // General API failure (success: false) without specific meter or field errors —
    // surface the message as a validation error so the caller can show it to the user.
    if (!(response as any)?.success) {
      const apiFailureMessage: string = (response as any)?.message ?? '';
      if (apiFailureMessage) {
        return {documentId: 0, meterErrors: [], validationErrors: [apiFailureMessage]};
      }
    }

    const successMessage: string =
      (response as any)?.message ?? result?.message ?? '';
    const warningMessage: string = result?.warningMessage ?? '';
    return {documentId: result?.documentId ?? 0, meterErrors: [], validationErrors: [], successMessage, warningMessage};
  } catch (error: any) {
    // Some API clients surface validation/4xx responses as thrown errors.
    // 1. Try to extract general field-level errors first.
    const errBody = error?.response?.data as any;
    const thrownValidationErrors: string[] = Array.isArray(errBody?.errors)
      ? (errBody.errors as string[])
      : [];
    if (thrownValidationErrors.length > 0) {
      return {documentId: 0, meterErrors: [], validationErrors: thrownValidationErrors};
    }
    // 2. Try to extract meterErrors from the error response body.
    const errResult = errBody?.data as CreateWorkOrderApiData | undefined;
    if (errResult?.meterErrors?.length) {
      return {documentId: 0, meterErrors: errResult.meterErrors, validationErrors: []};
    }
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Fetches meter readings for a given asset from /api/assets/meters?assetId={assetId}.
 * Maps each meter to a MeterReadingEntry: meterType from meterDesc, actualReading
 * from actualReading, reading/error/actionValue start empty (user input / API response).
 *
 * @param assetId - The asset's numeric ID.
 * @returns Promise resolving to MeterReadingEntry[].
 */
export const getAssetMeters = async (
  assetId: number,
): Promise<MeterReadingEntry[]> => {
  try {
    if (appConstants.isMock) {
      return [];
    }
    const {data} = await apiGet<AssetMeterApiItem[]>({
      endpoint: endPoints.createFlow.getAssetMeters(assetId),
    });
    const response = data as ApiResponse<AssetMeterApiItem[]>;
    const items: AssetMeterApiItem[] | undefined = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(data)
      ? (data as unknown as AssetMeterApiItem[])
      : undefined;
    if (items) {
      return items.map(m => ({
        id: String(m.meterID),
        meterType: m.meterDesc,
        reading: '',
        actualReading: String(m.actualReading),
        error: '',
        actionValue: '',
        availableOptions: [],
        isRequiredWorkOrder: m.isRequiredWorkOrder
      }));
    }
    return [];
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Fetches priority options for a work order.
 * Delegates to getAllRepairPriorities() (shared with the Dashboard) and maps
 * RepairPriorities[] → DropdownOption[] using priorityDesc as the label and
 * priorityCode as the value.
 * Returns mock data when appConstants.isMock is true.
 *
 * @returns Promise resolving to DropdownOption[].
 */
export const getWorkOrderPriorities = async (): Promise<DropdownOption[]> => {
  try {
    if (appConstants.isMock) {
      return workOrderFlowData.data.workOrderPriorities as DropdownOption[];
    }
    const priorities = await getAllRepairPriorities();
    if (priorities && priorities.length > 0) {
      return priorities.map(p => ({
        label: `${p.priorityDesc} [${p.priorityCode}]`,
        value: p.priorityID,
      }));
    }
    return [];
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

// ─── Repair Creation (Step 4) API functions ──────────────────────────────────

/**
 * Fetches overdue preventive maintenance items for the repair creation step.
 * Returns an empty array if none exist (section is hidden when empty).
 *
 * @returns Promise resolving to RepairSourceOption[].
 */
export const getRepairCreationOverduePM = async (
  assetId?: number,
): Promise<DropdownOption[]> => {
  try {
    if (!appConstants.isMock) {
      return workOrderFlowData.data
        .repairCreationOverduePM as unknown as DropdownOption[];
    }
    const {data} = await apiGet<DropdownOption[]>({
      endpoint: endPoints.createFlow.getRepairCreationOverduePM(assetId ?? 0),
    });
    const response = data as ApiResponse<DropdownOption[]>;
    if (response?.data) {
      return response.data;
    }
    return [];
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Fetches scheduled actions past due for the repair creation step.
 * Returns an empty array if none exist (section is hidden when empty).
 *
 * @returns Promise resolving to DropdownOption[].
 */
export const getRepairCreationScheduledActions = async (
  assetId?: number,
): Promise<DropdownOption[]> => {
  try {
    if (!appConstants.isMock) {
      return workOrderFlowData.data
        .repairCreationScheduledActions as unknown as DropdownOption[];
    }
    const {data} = await apiGet<DropdownOption[]>({
      endpoint: endPoints.createFlow.getRepairCreationScheduledActions(
        assetId ?? 0,
      ),
    });
    const response = data as ApiResponse<DropdownOption[]>;
    if (response?.data) {
      return response.data;
    }
    return [];
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Fetches repair schedule options for the repair creation step.
 *
 * @returns Promise resolving to DropdownOption[].
 */
export const getRepairSchedules = async (): Promise<DropdownOption[]> => {
  try {
    if (appConstants.isMock) {
      return workOrderFlowData.data.repairSchedules as DropdownOption[];
    }
    const {data} = await apiGet<DropdownOption[]>({
      endpoint: endPoints.createFlow.getRepairSchedules,
    });
    const response = data as ApiResponse<DropdownOption[]>;
    if (response?.data) {
      return response.data;
    }
    return workOrderFlowData.data.repairSchedules as DropdownOption[];
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Fetches maintenance shop options for the repair creation step.
 *
 * @returns Promise resolving to DropdownOption[].
 */
export const getRepairMaintenanceShops = async (): Promise<
  DropdownOption[]
> => {
  try {
    if (appConstants.isMock) {
      return workOrderFlowData.data.repairMaintenanceShops as DropdownOption[];
    }
    const {data} = await apiGet<DropdownOption[]>({
      endpoint: endPoints.createFlow.getRepairMaintenanceShops,
    });
    const response = data as ApiResponse<DropdownOption[]>;
    if (response?.data) {
      return response.data;
    }
    return workOrderFlowData.data.repairMaintenanceShops as DropdownOption[];
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

// /**
//  * Fetches repair action options (Repair Code — Action field).
//  *
//  * @returns Promise resolving to DropdownOption[].
//  */
// export const getRepairActions = async (): Promise<DropdownOption[]> => {
//   try {
//     if (appConstants.isMock) {
//       return workOrderFlowData.data.repairActions as DropdownOption[];
//     }
//     const {data} = await apiGet<DropdownOption[]>({
//       endpoint: endPoints.createFlow.getRepairActions,
//     });
//     const response = data as ApiResponse<DropdownOption[]>;
//     if (response?.data) {
//       return response.data;
//     }
//     return workOrderFlowData.data.repairActions as DropdownOption[];
//   } catch (error: any) {
//     globalErrorHandler(error);
//     throw error;
//   }
// };

// /**
//  * Fetches repair group options (Repair Code — Group field).
//  *
//  * @returns Promise resolving to DropdownOption[].
//  */
// export const getRepairGroups = async (): Promise<DropdownOption[]> => {
//   try {
//     if (appConstants.isMock) {
//       return workOrderFlowData.data.repairGroups as DropdownOption[];
//     }
//     const {data} = await apiGet<DropdownOption[]>({
//       endpoint: endPoints.createFlow.getRepairGroups,
//     });
//     const response = data as ApiResponse<DropdownOption[]>;
//     if (response?.data) {
//       return response.data;
//     }
//     return workOrderFlowData.data.repairGroups as DropdownOption[];
//   } catch (error: any) {
//     globalErrorHandler(error);
//     throw error;
//   }
// };

// /**
//  * Fetches repair component options (Repair Code — Component field).
//  *
//  * @returns Promise resolving to DropdownOption[].
//  */
// export const getRepairComponents = async (): Promise<DropdownOption[]> => {
//   try {
//     if (appConstants.isMock) {
//       return workOrderFlowData.data.repairComponents as DropdownOption[];
//     }
//     const {data} = await apiGet<DropdownOption[]>({
//       endpoint: endPoints.createFlow.getRepairComponents,
//     });
//     const response = data as ApiResponse<DropdownOption[]>;
//     if (response?.data) {
//       return response.data;
//     }
//     return workOrderFlowData.data.repairComponents as DropdownOption[];
//   } catch (error: any) {
//     globalErrorHandler(error);
//     throw error;
//   }
// };

/**
 * Fetches shift options for the repair creation step.
 *
 * @returns Promise resolving to DropdownOption[].
 */
export const getRepairShifts = async (): Promise<DropdownOption[]> => {
  try {
    if (appConstants.isMock) {
      return workOrderFlowData.data.repairShifts as DropdownOption[];
    }
    const {data} = await apiGet<DropdownOption[]>({
      endpoint: endPoints.createFlow.getRepairShifts,
    });
    const response = data as ApiResponse<DropdownOption[]>;
    if (response?.data) {
      return response.data;
    }
    return workOrderFlowData.data.repairShifts as DropdownOption[];
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Fetches priority options for the repair creation step.
 *
 * @returns Promise resolving to DropdownOption[].
 */
export const getRepairPriorities = async (): Promise<DropdownOption[]> => {
  try {
    if (appConstants.isMock) {
      return workOrderFlowData.data.repairPriorities as DropdownOption[];
    }
    const {data} = await apiGet<DropdownOption[]>({
      endpoint: endPoints.createFlow.getRepairPriorities,
    });
    const response = data as ApiResponse<DropdownOption[]>;
    if (response?.data) {
      return response.data;
    }
    return workOrderFlowData.data.repairPriorities as DropdownOption[];
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Fetches all repair-form dropdown options that come from the combined
 * /api/LookUps/repair-form-options endpoint in a single network call.
 *
 * Replaces five individual lookups:
 *   - Overdue Preventive Maintenance  (Maintenance Item Past Due)
 *   - Scheduled Action Past Due
 *   - Repair Schedule
 *   - Assign Technician
 *   - Shift
 *
 * @param assetId    The asset's numeric ID.
 * @param documentId The work order document ID (0 for new work orders).
 * @returns Object with five DropdownOption[] arrays.
 */
export const getRepairFormOptions = async (
  assetId: number,
  documentId: number,
): Promise<{
  overduePMs: DropdownOption[];
  scheduledActions: DropdownOption[];
  repairSchedules: DropdownOption[];
  technicians: DropdownOption[];
  shifts: DropdownOption[];
  timeStandards: DropdownOption[];
}> => {
  const empty = {
    overduePMs: [],
    scheduledActions: [],
    repairSchedules: [],
    technicians: [],
    shifts: [],
    timeStandards: [],
  };
  try {
    if (appConstants.isMock) {
      return {
        overduePMs: workOrderFlowData.data
          .repairCreationOverduePM as unknown as DropdownOption[],
        scheduledActions: workOrderFlowData.data
          .repairCreationScheduledActions as unknown as DropdownOption[],
        repairSchedules: workOrderFlowData.data
          .repairSchedules as DropdownOption[],
        technicians: [],
        shifts: workOrderFlowData.data.repairShifts as DropdownOption[],
        timeStandards: workOrderFlowData.data.timeStandards as DropdownOption[],
      };
    }
    const {data} = await apiGet<RepairFormOptionsApiResponse>({
      endpoint: endPoints.createFlow.getRepairFormOptions(assetId, documentId),
    });
    const response = data as ApiResponse<RepairFormOptionsApiResponse>;
    const result: RepairFormOptionsApiResponse | undefined =
      response?.data ?? (data as unknown as RepairFormOptionsApiResponse);

    if (!result) {
      return empty;
    }

    return {
      overduePMs: (result.pmDueItems ?? []).map(
        (item: PmDueApiItem): DropdownOption => ({
          label: item.pmDesc,
          value: String(item.repairGroupComponentActionID),
        }),
      ),
      scheduledActions: (result.saDueItems ?? []).map(
        (item: SaDueApiItem): DropdownOption => ({
          label: item.sa,
          value: String(item.repairGroupComponentActionID),
        }),
      ),
      repairSchedules: (result.repairSchedules ?? []).map(
        (item: RepairScheduleApiItem): DropdownOption => ({
          label: item.schedule,
          value: String(item.repairScheduleID),
        }),
      ),
      technicians: (result.technicians ?? []).map(
        (item: TechnicianApiItem): DropdownOption => ({
          label: item.personFullName,
          value: String(item.technicianID),
        }),
      ),
      shifts: (result.shifts ?? []).map(
        (item: ShiftApiItem): DropdownOption => ({
          label: item.laborShiftDesc,
          value: String(item.laborShiftID),
        }),
      ),
      timeStandards: (result.timeStandards ?? []).map(
        (item: TimeStandardApiItem): DropdownOption => ({
          label: item.text,
          value: String(item.value),
        }),
      ),
    };
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to fetch work order status
 * @param {repairId} - number
 * @returns {Promise<RepairReasonStatusesProps[]>}
 */
export const getRepairReasonStatus = async (): Promise<
  DropdownOption[] | undefined
> => {
  try {
    const {data} = await apiGet<RepairReasonStatusesProps[]>({
      endpoint: `${endPoints.repairDetails.repairReasonStatus}`,
    });
    const response = data as ApiResponse<RepairReasonStatusesProps[]>;
    const items: RepairReasonStatusesProps[] | undefined = Array.isArray(
      response?.data,
    )
      ? response.data
      : Array.isArray(data)
      ? (data as unknown as RepairReasonStatusesProps[])
      : undefined;
    if (items) {
      return items.map(s => ({
        label: s.reason,
        value: s.repairReasonId,
      }));
    }
    return [];
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Fetches time standard options for the repair creation step.
 *
 * @returns Promise resolving to DropdownOption[].
 */
/**
 * Fetches technicians for the given maintenance shop and maps them to DropdownOption[].
 * Re-uses the existing `/api/technicians?` endpoint (same as Shop Home Page).
 * Called each time the selected Maintenance Shop changes.
 *
 * @param shopId - The maintShop code/ID of the selected maintenance shop.
 * @returns Promise resolving to DropdownOption[] (label = fullName, value = technicianId).
 */
export const getRepairTechnicians = async (
  shopId: string,
): Promise<DropdownOption[]> => {
  try {
    if (appConstants.isMock) {
      // Re-use ShopHomePage mock technician data
      return mockTechniciansList.data.items.map(tech => ({
        label: tech.fullName,
        value: String(tech.technicianId),
      }));
    }
    const queryParams = new URLSearchParams({
      shopId,
      page: '1',
      pageSize: '1000',
      technicianName: '',
      isAscending: 'true',
    });
    const {data} = await apiGet<GetTechniciansListResponse>({
      endpoint: `${
        endPoints.shopHomePage.getTechnicians
      }${queryParams.toString()}`,
    });
    const response = data as ApiResponse<GetTechniciansListResponse>;
    const items = response?.data?.items ?? [];
    return items.map(tech => ({
      label: tech.fullName,
      value: tech.technicianId,
    }));
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

export const computeLabelMaxWidth = (text: string): number => {
  const charW = _LABEL_FONT_SIZE * _AVG_CHAR_FACTOR;
  const buffer = _LABEL_FONT_SIZE; // 1× font-size safety margin
  const words = text.split(' ');

  if (words.length <= 1) {
    return (words[0]?.length ?? text.length) * charW + buffer;
  }

  let best = Infinity;
  for (let i = 1; i < words.length; i++) {
    const w1 = words.slice(0, i).join(' ').length * charW;
    const w2 = words.slice(i).join(' ').length * charW;
    const mx = Math.max(w1, w2);
    if (mx < best) {
      best = mx;
    }
  }
  return best + buffer;
};

/**
 * Fetches work order success data after creating a work order.
 *
 * @returns Promise resolving to WorkOrderSuccessData.
 */
export const getWorkOrderSuccessData =
  async (): Promise<WorkOrderSuccessData> => {
    try {
      if (appConstants.isMock) {
        return workOrderFlowData.data.workOrderSuccess as WorkOrderSuccessData;
      }
      const {data} = await apiGet<WorkOrderSuccessData>({
        endpoint: endPoints.createFlow.getWorkOrderSuccessDetails,
      });
      const response = data as ApiResponse<WorkOrderSuccessData>;
      if (response?.data) {
        return response.data;
      }
      return workOrderFlowData.data.workOrderSuccess as WorkOrderSuccessData;
    } catch (error: any) {
      globalErrorHandler(error);
      // Fall back to mock data so the success screen always renders
      return workOrderFlowData.data.workOrderSuccess as WorkOrderSuccessData;
    }
  };

/**
 * Fetches the Work Order success-screen data via
 * GET /api/WorkOrderRepairs/{documentId}/repairs.
 *
 * The endpoint returns a **single** WorkOrderWithRepairsApiData object containing
 * asset, status, symptoms and an array of repairs.
 *
 * Field mapping:
 *   workOrderNumber → wo.documentId
 *   asset           → wo.assetNumber[wo.assetOrganization]
 *   status          → wo.workOrderStatusDesc [wo.workOrderStatusCode]
 *   symptoms        → wo.symptoms
 *   repairList      → wo.repairs[].{ repairDesc, createdDate, technicianName, repairCost }
 *
 * @param documentId  The work order document ID returned by saveWorkOrderDocument.
 * @param timezone    Windows timezone identifier (e.g. "India Standard Time"). Optional.
 * @returns Promise resolving to WorkOrderSuccessData, or null on error.
 */
export const fetchWorkOrderSuccessData = async (
  documentId: number,
  timezone?: string,
): Promise<WorkOrderSuccessData | null> => {
  try {
    if (appConstants.isMock) {
      return workOrderFlowData.data.workOrderSuccess as WorkOrderSuccessData;
    }

    const formatSuccessDate = (dateStr: string): string => {
      if (!dateStr) {
        return '';
      }
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) {
        return dateStr;
      }
      return d.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
      });
    };

    const params: Record<string, string> = {};
    if (timezone) {
      params.timezone = timezone;
    }

    // GET /api/WorkOrderRepairs/{documentId}/repairs — returns single object
    const {data} = await apiGet<WorkOrderWithRepairsApiData>({
      endpoint: endPoints.createFlow.getWorkOrderWithRepairs(documentId),
      params,
    });

    const response = data as ApiResponse<WorkOrderWithRepairsApiData>;
    const wo: WorkOrderWithRepairsApiData | undefined =
      response?.data ?? (data as unknown as WorkOrderWithRepairsApiData);

    if (!wo) {
      return null;
    }

    const repairs: WorkOrderWithRepairsRepairItem[] = Array.isArray(wo.repairs)
      ? wo.repairs
      : [];

    return {
      workOrderNumber: String(wo.documentId ?? documentId),
      asset: wo.assetOrganization
        ? `${wo.assetNumber}[${wo.assetOrganization}]`
        : (wo.assetNumber || '--'),
      status:
        wo.workOrderStatusDesc && wo.workOrderStatusCode
          ? `${wo.workOrderStatusDesc} [${wo.workOrderStatusCode}]`
          : (wo.workOrderStatusDesc || '--'),
      symptoms: wo.symptoms || '--',
      repairList: repairs.map(
        (r: WorkOrderWithRepairsRepairItem, idx: number) => ({
          id: String(r.repairID ?? idx + 1),
          repairNumber: idx + 1,
          repairDescription: r.repairDesc || '--',
          dateCreated: formatSuccessDate(r.createdDate || ''),
          technician: r.technicianName || '--',
          cost:
            r.repairCost != null
              ? `$${Number(r.repairCost).toFixed(3)}`
              : '$0.000',
        }),
      ),
    };
  } catch (_err) {
    return null;
  }
};

/**
 * Maps a single WorkOrderSearchApiItem from GET /api/workorders/search
 * to the RepairFlowWorkOrder shape consumed by SelectWorkOrder.
 */
const mapWorkOrderSearchToRepairFlow = (
  item: WorkOrderSearchApiItem,
  userTimezone: UserTimezone = {},
): RepairFlowWorkOrder => ({
  id: String(item.documentNumber) || '--',
  woNumber: String(item.documentNumber) || '--',
  woStatus: item.status || '--',
  description: item.description || '--',
  assetType: item.make ?? '--',
  assetNumber: item.assetNumber ?? '--',
  licenseNumber: item.license ?? '--',
  shop: item.maintShop ?? '--',
  dateCreated: formatDate(item.dateTimeIn ?? '', userTimezone, 'dateTimeWithYear'),
  assetId: item.assetId ?? 0,
  assetClass: item.assetClass ?? '',
});

/** Paginated result shape returned by getWorkOrdersForRepairFlow. */
export interface WorkOrderSearchPaginatedResult {
  items: RepairFlowWorkOrder[];
  totalCount: number;
}

/**
 * Fetches work orders for selection in the New Repair flow via
 * POST /api/workorders/search.
 *
 * Body params:
 *   search       — free-text term (asset number, license plate, WO number)
 *   shopId       — numeric shop ID; null means "All Shops"
 *   userTimeZone — Windows timezone identifier
 *   pageNumber   — 1-based page index
 *   pageSize     — number of records per page
 *   sortBy       — API field name to sort by (default 'documentNumber')
 *   isAscending  — sort direction (default false → descending)
 *
 * @param shopCode    - Shop ID value from the shop dropdown; omit for All Shops.
 * @param searchText  - Free-text search term.
 * @param userTimeZone - Windows timezone string.
 * @param pageNumber  - 1-based page index (default 1).
 * @param pageSize    - Records per page (default 9).
 * @param userTimezone - UserTimezone object for date formatting.
 * @param sortBy      - API sort field name (default 'documentNumber').
 * @param isAscending - Sort direction (default false).
 * @returns Promise resolving to WorkOrderSearchPaginatedResult.
 */
export const getWorkOrdersForRepairFlow = async (
  shopCode?: string,
  searchText?: string,
  userTimeZone?: string,
  pageNumber: number = 1,
  pageSize: number = 9,
  userTimezone: UserTimezone = {},
  sortBy: string = 'assetNumber',
  isAscending: boolean = false,
): Promise<WorkOrderSearchPaginatedResult> => {
  try {
    if (appConstants.isMock) {
      let items = repairFlowData.data.workOrders as RepairFlowWorkOrder[];
      if (shopCode) {
        const shopLabel = repairFlowData.data.shops.find(
          s => s.value === shopCode,
        )?.label;
        if (shopLabel) {
          items = items.filter(
            wo => wo.shop.toLowerCase() === shopLabel.toLowerCase(),
          );
        }
      }
      if (searchText) {
        const query = searchText.toLowerCase();
        items = items.filter(
          wo =>
            wo.assetNumber.toLowerCase().includes(query) ||
            wo.licenseNumber.toLowerCase().includes(query),
        );
      }
      const totalCount = items.length;
      const start = (pageNumber - 1) * pageSize;
      return {items: items.slice(start, start + pageSize), totalCount};
    }
    const payload: Record<string, any> = {
      userTimeZone: userTimeZone ?? null,
      shopId: shopCode ? Number(shopCode) : null,
      search: searchText ?? null,
      pageNumber,
      pageSize,
      sortBy,
      isAscending,
    };
    const {data} = await apiPost<any>({
      endpoint: endPoints.createFlow.getWorkOrdersForRepairFlow,
      payload,
    });
    // API response shape:
    // { success: true, data: WorkOrderSearchApiItem[], pagination: { totalItems, currentPage, pageSize, ... } }
    // `pagination` sits at the root of the response object alongside `data`.
    const response = data as any;
    if (response?.success) {
      const items: WorkOrderSearchApiItem[] = Array.isArray(response.data)
        ? response.data
        : [];
      const totalCount: number =
        response.pagination?.totalItems ?? items.length;
      return {
        items: items.map(item => mapWorkOrderSearchToRepairFlow(item, userTimezone)),
        totalCount,
      };
    }
    return {items: [], totalCount: 0};
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Calls POST /api/LookUps/repair-form/control-sync when the user changes a
 * key dropdown in the Repair Creation form (e.g. Overdue PM or Scheduled
 * Action).  The server returns default values for all other form fields so
 * the UI can pre-populate them automatically.
 *
 * @param controlChanged - Label key of the changed control (e.g. "PM", "SA").
 * @param selectedValue  - Numeric ID of the newly selected option.
 * @returns Promise resolving to RepairFormControlSyncResponse, or undefined on mock.
 */
export const getRepairFormControlSync = async (
  controlChanged: string,
  selectedValue: number,
  selectedClass: string,
  assetId: number
): Promise<RepairFormControlSyncResponse | undefined> => {
  try {
    if (appConstants.isMock) {
      // In mock mode return undefined so the caller falls back to local defaults.
      return undefined;
    }
    const payload: RepairFormControlSyncRequest = {
      controlChanged,
      selectedValue,
      class: selectedClass,
      assetId
    };
    const {data} = await apiPost<RepairFormControlSyncResponse>({
      endpoint: endPoints.createFlow.repairFormControlSync,
      payload,
    });
    const response = data as ApiResponse<RepairFormControlSyncResponse>;
    return response?.data ?? (data as unknown as RepairFormControlSyncResponse);
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Fetches the full Action/Group/Component (AGC) grid from
 * GET /api/lookups/repair-form/agc-grid.
 *
 * The flat response list is de-duplicated by ID to produce three independent
 * DropdownOption arrays for the Action, Group, and Component dropdowns in the
 * Repair Code card.
 *
 *   actionDesc  → label for the Action dropdown   (value = repairActionID)
 *   groupDesc   → label for the Group dropdown    (value = repairGroupID)
 *   componentDesc → label for Component dropdown  (value = repairComponentID)
 *
 * @returns Object with `actions`, `groups`, and `components` DropdownOption arrays.
 */
export const getRepairAGCGrid = async (): Promise<{
  actions: DropdownOption[];
  groups: DropdownOption[];
  components: DropdownOption[];
  rawItems: AGCGridApiItem[];
}> => {
  const empty = {actions: [], groups: [], components: [], rawItems: []};
  try {
    if (appConstants.isMock) {
      // Fall back to existing individual mock arrays so the form still works in mock mode.
      // rawItems is empty in mock mode as the mock data lacks the AGCGridApiItem shape.
      return {
        actions: workOrderFlowData.data.repairActions as DropdownOption[],
        groups: workOrderFlowData.data.repairGroups as DropdownOption[],
        components: workOrderFlowData.data.repairComponents as DropdownOption[],
        rawItems: [],
      };
    }
    const {data} = await apiGet<AGCGridApiItem[]>({
      endpoint: endPoints.createFlow.getRepairAGCGrid,
    });
    const response = data as ApiResponse<AGCGridApiItem[]>;
    const items: AGCGridApiItem[] | undefined = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(data)
      ? (data as unknown as AGCGridApiItem[])
      : undefined;

    if (!items || items.length === 0) {
      return empty;
    }

    // De-duplicate each dimension by its own numeric ID.
    // Values use the individual dimension IDs so the component can do bidirectional
    // filtering: any of the three dropdowns can be selected first, and each selection
    // narrows the other two to only the combinations that exist in the grid.
    // The final unique repairGroupComponentActionID is derived in the component once
    // all three selections are made.

    /**
     * Sort key for AGC dropdown labels.
     *
     * Labels that contain a dash-letter token such as "-L" or "-B" (e.g.
     * "Repair -L", "-B Item") are sorted under the section of that letter
     * but AFTER all plain entries starting with the same letter, by appending
     * the Unicode high-value character '\uFFFF' as a tie-breaker.
     *
     * Examples (ascending order):
     *   "Lube"      → key "Lube"         → sorts normally under L
     *   "Repair -L" → key "L\uFFFFRepair -L" → sorts under L, after plain L entries
     *   "Brake -B"  → key "B\uFFFFBrake -B"  → sorts under B, after plain B entries
     */
    const agcSortKey = (label: string): string => {
      // Match labels that BEGIN with a dash followed by a letter (e.g. "-Break",
      // "-Lunch").  These sort under that letter's section but AFTER all plain
      // entries starting with the same letter ('\uFFFF' acts as a high tie-breaker).
      const match = label.match(/^-([A-Za-z])/);
      if (match && match[1]) {
        return match[1].toUpperCase() + '\uFFFF' + label;
      }
      return label;
    };

    const actionMap = new Map<number, DropdownOption>();
    const groupMap = new Map<number, DropdownOption>();
    const componentMap = new Map<number, DropdownOption>();

    for (const item of items) {
      if (!actionMap.has(item.repairActionID)) {
        actionMap.set(item.repairActionID, {
          label: item.actionDesc,
          value: item.repairActionID,
        });
      }
      if (!groupMap.has(item.repairGroupID)) {
        groupMap.set(item.repairGroupID, {
          label: item.groupDesc,
          value: item.repairGroupID,
        });
      }
      if (!componentMap.has(item.repairComponentID)) {
        componentMap.set(item.repairComponentID, {
          label: item.componentDesc,
          value: item.repairComponentID,
        });
      }
    }

    return {
      actions: Array.from(actionMap.values()).sort((a, b) =>
        agcSortKey(a.label as string).localeCompare(
          agcSortKey(b.label as string),
        ),
      ),
      groups: Array.from(groupMap.values()).sort((a, b) =>
        agcSortKey(a.label as string).localeCompare(
          agcSortKey(b.label as string),
        ),
      ),
      components: Array.from(componentMap.values()).sort((a, b) =>
        agcSortKey(a.label as string).localeCompare(
          agcSortKey(b.label as string),
        ),
      ),
      rawItems: items,
    };
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Fetches warranty items for the given asset.
 * In mock mode, returns all warranty items from the mock JSON.
 * In live mode, calls GET /api/assets/{assetId}/warranties and maps
 * the nested response (data.warranties[]) to WarrantyItem[].
 *
 * API response shape:
 *   { success, data: { assetId, warranties: WarrantyApiItem[] }, ... }
 *
 * Field mapping:
 *   assetWarrantyId   → id
 *   deductibleCost    → deductible (formatted as "$X")
 *   cost              → cost (formatted as "$X")
 *   cycles[].cycleTypeDescription → cycleType
 *   cycles[].cycleLength (number) → cycleLength (formatted string)
 *   cycles[].begins / expires     → formatted date strings
 *
 * @param assetId - The asset's numeric ID.
 * @returns Promise resolving to WarrantyItem[].
 */
export const getWarranties = async (
  assetId: number,
  userTimezone: UserTimezone = {},
): Promise<WarrantyItem[]> => {
  try {
    if (appConstants.isMock) {
      return workOrderFlowData.data.warranties as WarrantyItem[];
    }
    const {data} = await apiGet<{assetId: number; warranties: any[]}>({
      endpoint: endPoints.createFlow.getWarranties(assetId),
    });
    const response = data as ApiResponse<{assetId: number; warranties: any[]}>;
    const rawWarranties: any[] = response?.data?.warranties ?? [];
    if (rawWarranties.length > 0) {
      return rawWarranties.map((w: any): WarrantyItem => ({
        id: String(w.assetWarrantyId),
        warrantyCategory: w.warrantyCategory ?? '',
        warrantyType: w.warrantyType ?? '',
        description: w.description ?? '',
        deductible: `$${Number(w.deductibleCost ?? 0).toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}`,
        cost: `$${Number(w.cost ?? 0).toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}`,
        vendor: w.vendor ?? '',
        referenceNumber: w.referenceNumber ?? '',
        cycles: (w.cycles ?? []).map((c: any): WarrantyCycle => ({
          cycleType: c.cycleTypeDescription ?? c.cycleType ?? '',
          cycleLength: Number(c.cycleLength ?? 0).toLocaleString('en-US'),
          begins: formatDate(c.begins ?? '', userTimezone, 'dateTimeWithSeconds'),
          expires: formatDate(c.expires ?? '', userTimezone, 'dateTimeWithSeconds'),
        })),
      }));
    }
    return [];
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * PATCHes the generated barcode image onto the saved work order.
 * Calls PATCH /api/workorders/{documentId} with userId, userName and the
 * raw base64 PNG string (no data-URI prefix).
 *
 * @param documentId   - The work order document ID returned by saveWorkOrderDocument.
 * @param userId       - Numeric user ID from Redux state.user.
 * @param userName     - Display name from Redux state.user.
 * @param barcodeImage - Pure base64 PNG string (no "data:image/png;base64," prefix).
 */
export const updateWorkOrderBarcode = async (
  documentId: number,
  userId: number,
  userName: string,
  barcodeImage: string,
): Promise<void> => {
  try {
    if (appConstants.isMock) {
      return; // no-op in mock mode
    }
    await apiPatch<unknown>({
      endpoint: endPoints.createFlow.updateWorkOrderBarcode(documentId),
      payload: {userId, userName, barcodeImage},
    });
  } catch (error: any) {
    // Non-fatal: log the error but do not block the user from proceeding.
    globalErrorHandler(error);
  }
};

/**
 * Calls POST /api/WorkOrderRepairs/Create to create a new repair on a work order.
 *
 * Returns a CreateRepairResult:
 *   { success: true, repairId: <id> }   on success
 *   { success: false, message: <msg> }  when the API returns a failure
 *
 * In mock mode returns a synthetic success result so the form can be tested
 * without a live server.
 *
 * @param payload - Full CreateRepairPayload built from Step-4 form state.
 * @returns Promise<CreateRepairResult>
 */
// ─── Compatibility shims — individual named exports consumed by tests ─────────
// These thin wrappers delegate to the combined API functions so that Jest
// can spy on each export by its legacy name while the component still uses
// the more-efficient combined endpoints.

/**
 * Returns repair-reason / status options.
 * Delegates to getRepairReasonStatus() and unwraps the result.
 * @returns Promise resolving to DropdownOption[].
 */
export const getRepairReasons = async (): Promise<DropdownOption[]> => {
  const result = await getRepairReasonStatus();
  return result ?? [];
};

/**
 * Returns repair-action (AGC) options.
 * Delegates to getRepairAGCGrid() and returns the `actions` slice.
 * @returns Promise resolving to DropdownOption[].
 */
export const getRepairActions = async (): Promise<DropdownOption[]> => {
  const {actions} = await getRepairAGCGrid();
  return actions;
};

/**
 * Returns repair-group (AGC) options.
 * Delegates to getRepairAGCGrid() and returns the `groups` slice.
 * @returns Promise resolving to DropdownOption[].
 */
export const getRepairGroups = async (): Promise<DropdownOption[]> => {
  const {groups} = await getRepairAGCGrid();
  return groups;
};

/**
 * Returns repair-component (AGC) options.
 * Delegates to getRepairAGCGrid() and returns the `components` slice.
 * @returns Promise resolving to DropdownOption[].
 */
export const getRepairComponents = async (): Promise<DropdownOption[]> => {
  const {components} = await getRepairAGCGrid();
  return components;
};

/**
 * Returns time-standard options from the combined repair-form-options endpoint.
 * Delegates to getRepairFormOptions() and returns the `timeStandards` slice.
 * @returns Promise resolving to DropdownOption[].
 */
export const getRepairTimeStandards = async (): Promise<DropdownOption[]> => {
  const {timeStandards} = await getRepairFormOptions(0, 0);
  return timeStandards;
};

export const createRepair = async (
  payload: CreateRepairPayload,
): Promise<CreateRepairResult> => {
  try {
    if (appConstants.isMock) {
      return {success: true, repairId: 0, message: 'Repair created successfully.'};
    }
    const {data} = await apiPost<CreateRepairInnerData>({
      endpoint: endPoints.createFlow.createRepair,
      payload,
    });
    const response = data as ApiResponse<CreateRepairInnerData>;
    const inner: CreateRepairInnerData | undefined =
      response?.data ?? (data as unknown as CreateRepairInnerData);

    const apiSuccess =
      (response as any)?.success !== false &&
      inner?.success !== false;

    return {
      success: apiSuccess,
      repairId: inner?.workOrderRepairId ?? inner?.repairId,
      message:
        (response as any)?.message ??
        inner?.message ??
        '',
    };
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};
